import { useEffect, useRef } from 'preact/hooks';
import type { ComponentChildren } from 'preact';

interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ComponentChildren;
  hideClose?: boolean;
}

export function Modal({ open, title, onClose, children, hideClose = false }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus the first interactive element inside the dialog when it opens.
  // Falling back to the dialog container ensures at least something receives focus.
  useEffect(() => {
    if (!open || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    (focusable ?? dialogRef.current).focus();
  }, [open]);

  // Escape closes. Tab is trapped inside the dialog.
  useEffect(() => {
    if (!open || !dialogRef.current) return;
    const dialog = dialogRef.current;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;

      const focusableEls = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (focusableEls.length === 0) return;

      const first = focusableEls[0];
      const last = focusableEls[focusableEls.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        class="absolute inset-0 bg-black/50"
        data-testid="modal-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
        class="relative z-10 bg-white rounded-xl shadow-xl max-w-sm w-full p-6 focus:outline-none"
      >
        <h2 id="modal-title" class="text-lg font-bold text-hudson-navy mb-4">
          {title}
        </h2>
        {children}
        {!hideClose && (
          <button
            type="button"
            onClick={onClose}
            class="absolute top-4 right-4 text-body/70 hover:text-body focus:outline-none focus:ring-2 focus:ring-hudson-navy rounded"
            aria-label="Close dialog"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}
